import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { createContainer } from 'unstated-next';
import { getSpecialPrice } from '../components/PriceTagSimple/PriceTagSimple';
import { ApiProduct, ApiSpecialsProduct, ApiSpecialSuggestions, ApiUser } from '../types';
import { isRedirect, Res, useApi } from './api';

export interface IndexedSpecialPrices {
	[productId: number]: ApiSpecialsProduct;
}
interface Products {
	categoryPerma: string;
	setCategoryPerma(category: string): void;
	specialPricesIndex: IndexedSpecialPrices;
	getFinalPrice(productId: number, standardPrice: number, quantity?: number, campaignPrice?: number): number;
	getSpecialPriceSuggestion(productId: number): ApiSpecialSuggestions | undefined;
	getSpecialsProducts(products: ApiProduct[]): (ApiProduct | ApiSpecialsProduct)[];
}

function buildProduct(): Products {
	const perma = useLocation().pathname.slice(1);
	const isProductPerma = /-p-/g.test(perma);
	const { data: resp } = useApi(isProductPerma ? Res.product : null, { perma });
	const data = isRedirect(resp) ? undefined : resp;
	let [categoryPerma, setCategoryPerma] = useState(data?.categoryPerma ?? '');
	let { data: sessionData } = useApi(Res.session, {});
	const { data: specialPriceSuggestions } = useApi(sessionData ? Res.specialPricesSuggestions : null, {});

	let hasSpecials = false;
	if (typeof sessionData === 'object') {
		sessionData = sessionData as ApiUser;
		hasSpecials = sessionData.specials;
	}
	const { data: specialPrices } = useApi(hasSpecials ? Res.specialsWithProducts : null, {});
	const [specialPricesIndex, setSpecialPricesIndex] = useState<IndexedSpecialPrices>({});

	useEffect(() => {
		if (specialPrices) {
			let indexedSpecialPrices: IndexedSpecialPrices = {};
			specialPrices.forEach((special) => {
				indexedSpecialPrices[special.productId] = special;
			});
			setSpecialPricesIndex(indexedSpecialPrices);
		}
	}, [specialPrices]);

	/*
	 * One object per set of values rather than one per render. Every product box reads this
	 * container, and a container that hands out a fresh object re-renders all of them whatever
	 * memo says - which is what happens on "Visa fler", since the hook above reads the location
	 * and the button is a link.
	 */
	return useMemo(
		() => ({
			categoryPerma: categoryPerma,
			setCategoryPerma(categoryPerma) {
				setCategoryPerma(categoryPerma);
			},
			specialPricesIndex,
			getFinalPrice(productId, standardPrice, quantity = 1, campaignPrice = 0) {
				const specialPrice = getSpecialPrice(productId);
				if (specialPrice) return specialPrice * quantity;
				if (campaignPrice > 0) return campaignPrice * quantity;

				return standardPrice * quantity;
			},
			getSpecialPriceSuggestion(productId) {
				return specialPriceSuggestions?.find((item) => item.product === Number(productId));
			},
			getSpecialsProducts(products: ApiProduct[]): (ApiProduct | ApiSpecialsProduct)[] {
				if (!products || products.length === 0) return [];
				if (specialPricesIndex) {
					return products.map((product) => specialPricesIndex[product.id] || product);
				}
				return products;
			},
		}),
		[categoryPerma, specialPricesIndex, specialPriceSuggestions]
	);
}

export const ProductContainer = createContainer(buildProduct);
